import React from 'react';
import clsx from 'clsx';
import moment from 'moment';
import Typography from '@material-ui/core/Typography';
import Skeleton from '@material-ui/lab/Skeleton';

import { makeStyles } from '@material-ui/core/styles';

import { numberWithDots } from '../../../Utils/Format/MoneyFormat';
import { Divider, Grid } from '@material-ui/core';
import { ROUTE_NAMES } from '../../../Routes/Routes';
import { history } from '../../../Routes/history';

import BackIcon from '../../../Components/CustomIcons/BackIcon';
import FlatButton from '../../Buttons/FlatButton';
import BaseTooltip from '../../Tooltip/BaseTooltip';
import EyeIcon from '../../CustomIcons/EyeIcon';
import { OpenNewTab } from '../../../Utils/Misc/Links';

const handlePayCouponButtonClick = () => {
  history.push(ROUTE_NAMES.payments, {
    type: 'coupon'
  });
};

const ConstructorInvoiceLabel = ({
  isPaid,
  isPending,
  showTotalTooltip,
  classes
}) => {
  if (isPaid) {
    return (
      <Typography
        className={clsx(
          classes.labelText,
          classes.payedText,
          classes.alignRight
        )}
      >
        Pagado
      </Typography>
    );
  }
  if (isPending) {
    return (
      <Typography
        className={clsx(
          classes.labelText,
          classes.pendingText,
          classes.alignRight
        )}
      >
        En proceso
      </Typography>
    );
  }
  return (
    <>
      <Typography
        className={clsx(classes.labelText, classes.alignRightTotalValue)}
      >
        Total a pagar
        {showTotalTooltip && (
          <>
            &nbsp;
            <BaseTooltip
              tooltipText={
                'Este valor hace referencia al valor de la factura del último mes facturado'
              }
              showTooltipOnClick
              startWithBreakline
            />
          </>
        )}
      </Typography>
    </>
  );
};

const ConstructorInvoiceExpirationLabel = ({ expirationDate, classes }) => {
  let text = 'Venció';
  const dateText = moment(expirationDate).fromNow();
  if (moment(expirationDate).diff() > 0) {
    text = 'Vence';
  }

  return (
    <Typography
      title={moment(expirationDate).format('L')}
      className={classes.subText}
    >
      {text} {dateText}
    </Typography>
  );
};

const ConstructorInvoiceCardActions = ({ isPaid, isPending, url, classes }) => {
  const disablePayment = isPaid || isPending;
  const hasUrl = Boolean(url);

  if (disablePayment && !hasUrl) {
    return <></>;
  }

  return (
    <div className={classes.actionsContainer}>
      <Grid container justifyContent="space-between" spacing={2}>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item>
          {hasUrl && (
            <FlatButton
              id="ConstructorInvoiceCard_button_downloadPayment"
              color="primary"
              className={classes.actionButton}
              onClick={() => OpenNewTab(url)}
            >
              Ver factura
              <EyeIcon size={16} className={classes.icon} />
            </FlatButton>
          )}
        </Grid>
        <Grid item>
          {!disablePayment && (
            <FlatButton
              id="ConstructorInvoiceCard_button_payCouponRedirect"
              color="primary"
              className={classes.actionButton}
              onClick={handlePayCouponButtonClick}
            >
              Pagar factura
              <BackIcon
                size={16}
                className={clsx(classes.icon, classes.iconRotated)}
              />
            </FlatButton>
          )}
        </Grid>
      </Grid>
    </div>
  );
};

const ConstructorInvoiceCardGDC = props => {
  const classes = useStyles();
  const {
    id,
    invoiceDetails,
    className,
    loading = false,
    showTotalTooltip = false,
    disableActions = false
  } = props;

  if (loading) {
    return (
      <div className={clsx(classes.couponRoot, className)}>
        <div className={clsx(classes.cardHalf, classes.skeletonTop)}>
          <Skeleton className={classes.skeletonTop} />
          <div className={classes.skeletonCardBottom}>
            <Skeleton className={classes.skeletonBottom} />
            <Skeleton className={classes.skeletonBottom} />
          </div>
        </div>
      </div>
    );
  }

  if (!invoiceDetails) {
    return (
      <div className={clsx(classes.couponRoot, className)}>
        <Typography className={classes.errorTitle}>
          <span role="img" aria-label="construction">
            🚧
          </span>{' '}
          En el momento no pudimos obtener tu factura{' '}
          <span role="img" aria-label="construction">
            🚧
          </span>
        </Typography>
        <Grid container>
          <Grid item xs={12}>
            <Typography className={classes.errorBody}>
              Estamos trabajando para mejorar tu experiencia{' '}
              <span role="img" aria-label="constructio_worker">
                👷
              </span>
              .
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography className={classes.errorBody}>
              Puedes seguir realizando los pagos de tu factura con tu número de
              cupón.
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            className={clsx(classes.payCouponButton, classes.alignRight)}
          >
            <FlatButton
              id="ConstructorInvoiceCard_button_payCouponRedirect"
              color="primary"
              className={classes.actionButton}
              onClick={handlePayCouponButtonClick}
            >
              Pagar factura
              <BackIcon
                size={16}
                className={clsx(classes.icon, classes.iconRotated)}
              />
            </FlatButton>
          </Grid>
        </Grid>
      </div>
    );
  }

  return (
    <div id={id} className={clsx(classes.couponRoot, className)}>
      <div className={clsx(classes.cardHalf, classes.leftHalf)}>
        <Grid container>
          <Grid item xs={12}>
            <Typography className={classes.dateText}>
              {moment([invoiceDetails.year, invoiceDetails.month - 1]).format(
                'MMMM YYYY'
              )}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography className={classes.subText}>
              Factura nº {invoiceDetails.id}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {!invoiceDetails.isPaid && (
              <ConstructorInvoiceExpirationLabel
                expirationDate={invoiceDetails.expirationDate}
                classes={classes}
              />
            )}
          </Grid>
        </Grid>
        <div className={classes.infoContainer}></div>
      </div>
      <div className={clsx(classes.cardHalf, classes.rightHalf)}>
        <ConstructorInvoiceLabel
          isPaid={invoiceDetails.isPaid}
          isPending={invoiceDetails.isPending}
          showTotalTooltip={showTotalTooltip}
          classes={classes}
        />
        <Typography className={clsx(classes.valueText, classes.alignRight)}>
          {numberWithDots(invoiceDetails.value)}
        </Typography>
      </div>
      {!disableActions && (
        <ConstructorInvoiceCardActions
          isPaid={invoiceDetails.isPaid}
          isPending={invoiceDetails.isPending}
          url={invoiceDetails.url}
          classes={classes}
        />
      )}
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  couponRoot: {
    display: 'flex',
    flexFlow: 'row wrap',
    backgroundColor: theme.palette.background.cardDark,
    border: 'solid 1px #dbdbdb',
    borderRadius: theme.custom.borderRadius,
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    [`@media (max-width:${theme.breakpoints.values.sm - 1}px)`]: {
      paddingTop: theme.spacing(2.5),
      paddingBottom: theme.spacing(2.5),
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3)
    },
    [`@media (max-width:${theme.breakpoints.values.xm - 1}px)`]: {
      paddingTop: theme.spacing(1.5),
      paddingBottom: theme.spacing(1.5),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2)
    }
  },
  cardHalf: {
    display: 'flex',
    flexDirection: 'column'
  },
  rightHalf: {
    flex: 0.4,
    alignSelf: 'center'
  },
  leftHalf: {
    flex: 0.6
  },
  actionsContainer: {
    flexBasis: '100%',
    marginTop: theme.spacing(2)
  },
  dateText: {
    fontSize: 18,
    fontWeight: 500,
    textTransform: 'capitalize',
    color: theme.palette.text.primary,
    [`@media (max-width:${theme.breakpoints.values.sm - 1}px)`]: {
      fontSize: 16
    },
    [`@media (max-width:${theme.breakpoints.values.xm - 1}px)`]: {
      fontSize: 14
    }
  },
  valueText: {
    fontSize: 28,
    fontWeight: 500,
    color: theme.palette.text.default,
    whiteSpace: 'nowrap',
    [`@media (max-width:${theme.breakpoints.values.sm - 1}px)`]: {
      fontSize: 23
    },
    [`@media (max-width:${theme.breakpoints.values.xm - 1}px)`]: {
      fontSize: 18
    }
  },
  labelText: {
    fontSize: 14,
    fontWeight: 600,
    [`@media (max-width:${theme.breakpoints.values.sm - 1}px)`]: {
      fontSize: 13
    },
    [`@media (max-width:${theme.breakpoints.values.xm - 1}px)`]: {
      fontSize: 12
    }
  },
  payedText: {
    color: theme.palette.success.main
  },
  pendingText: {
    color: theme.palette.warning.main
  },
  infoContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between'
  },
  subText: {
    display: 'inline',
    fontSize: 12,
    paddingTop: theme.spacing(),
    paddingRight: theme.spacing(),
    color: theme.palette.text.greyDark,
    [`@media (max-width:${theme.breakpoints.values.sm - 1}px)`]: {
      paddingTop: theme.spacing(0.3)
    },
    [`@media (max-width:${theme.breakpoints.values.xm - 1}px)`]: {
      paddingTop: theme.spacing(0.3),
      fontSize: 10,
      paddingRight: 0
    }
  },
  alignRight: {
    textAlign: 'right'
  },
  alignRightTotalValue: {
    textAlign: 'right',
    display: 'flex',
    width: 'auto',
    alignSelf: 'end',
    alignItems: 'center',
    justifyContent: 'center'
  },
  skeletonTop: {
    width: '100%'
  },
  skeletonCardBottom: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between'
  },
  skeletonBottom: {
    width: '45%'
  },
  errorTitle: {
    fontSize: 16,
    fontWeight: 500,
    color: theme.palette.text.primary,
    [`@media (max-width:${theme.breakpoints.values.sm - 1}px)`]: {
      fontSize: 14
    }
  },
  errorBody: {
    fontSize: 14,
    color: theme.palette.text.primary,
    marginTop: theme.spacing()
  },
  payCouponButton: {
    marginTop: theme.spacing(2)
  },
  icon: {
    fill: theme.palette.primary.main,
    marginLeft: theme.spacing()
  },
  iconRotated: {
    transform: 'rotate(180deg)'
  },
  actionButton: {
    fontWeight: 500,
    fontSize: 12,
    color: theme.palette.common.black,
    textTransform: 'none',
    [theme.breakpoints.up('sm')]: {
      fontSize: 14
    }
  }
}));

export default ConstructorInvoiceCardGDC;
